import { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { Button, Column, DataTable, Hyperlink, IconButton } from "@racoon/ui";
import { IcPencil, IcPlus } from "@racoon/icons";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { formatTenge } from "@racoon/lib";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { SectionCaption } from "~/shared/ui/section-caption";
import { useCustomerCardModal } from "~/features/customer-card/hooks/use-customer-card-modal";
import { useOpenContract } from "~/features/customer-card/hooks/use-open-contract";
import { useAttachByNormModal } from "./attach-by-norm.modal";
import { ContainerGroupModal } from "./container-group.modal";
import { useContainerGroupSubmit } from "../hooks/use-container-group-submit";
import { useLocationSubscriptions } from "../hooks/use-location-subscriptions";
import { contractSpanLabel, contractTypeLabel } from "../model/contract-label";
import { LocationTableFrame, SaveLocationFirst, TableEmpty } from "./location-table-frame";

/** The design shows links blue at rest, not only on hover. */
const LINK = "text-blue-600";

/**
 * Row pencil, shown on the hovered row (the rows carry the `group` class).
 * 24px rather than IconButton's 32px box, so the row keeps the design's height.
 */
const EditButton: FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <IconButton
    size="sm"
    aria-label={label}
    className="!size-6 !min-h-6 !min-w-6 !p-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <IcPencil className="text-blue-600" />
  </IconButton>
);

const Section: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <section className="flex flex-col gap-4">
    <SectionCaption>{title}</SectionCaption>
    {children}
  </section>
);

/**
 * Who is served at this location: owners of the container groups here, billed
 * by volume, and BY_NORM subscribers, who have no groups and pay a flat monthly fee.
 */
export const ParticipantsBlock: FC<{
  locationId?: number | string;
  containerGroups: ConnectProto.ContainerGroup[];
  isLoading?: boolean;
}> = ({ locationId, containerGroups, isLoading }) => {
  const t = useT();
  const locale = useDateFnsLocale();
  const { selectedCompanyId } = useSelectedCompany();
  const { openSingle: openCustomer } = useCustomerCardModal({
    providerId: selectedCompanyId ?? undefined,
  });
  const openAttachModal = useAttachByNormModal();
  const submitGroup = useContainerGroupSubmit();
  const [editingGroup, setEditingGroup] = useState<ConnectProto.ContainerGroup | null>(null);

  // BY_NORM contracts come preloaded on the location itself.
  const {
    pickupLocation,
    subscriptionContracts,
    isFetching: isLocationFetching,
  } = useLocationSubscriptions(locationId ? BigInt(locationId) : undefined);

  const openContract = useOpenContract();

  const customerCell = useCallback(
    (org?: ConnectProto.Organization) =>
      org ? (
        <Hyperlink className={LINK} onOpen={() => openCustomer(org)}>
          {org.name}
        </Hyperlink>
      ) : (
        "—"
      ),
    [openCustomer],
  );

  const ownerColumns = useMemo<Column<ConnectProto.ContainerGroup>[]>(
    () => [
      {
        id: "customer",
        header: t(Namespace.PickupLocation, "table.customer"),
        // ListContainerGroups preloads Contract but not its TargetOrganization;
        // the group's owner is the same customer.
        accessor: (group) => group.ownerOrganization?.name ?? "",
        cell: (info) => customerCell(info.row.original.ownerOrganization),
      },
      {
        id: "group",
        header: t(Namespace.PickupLocation, "groups.table.containerGroups"),
        accessor: (group) => `${group.containerType?.name ?? "—"} (${group.numberOfContainers})`,
        cell: (info) => (
          <Hyperlink className={LINK} onOpen={() => setEditingGroup(info.row.original)}>
            {info.getValue() as string}
          </Hyperlink>
        ),
      },
      {
        id: "contractType",
        header: t(Namespace.PickupLocation, "participants.table.contractType"),
        accessor: (group) => contractTypeLabel(group.contract?.type, t),
      },
      {
        id: "contract",
        header: t(Namespace.PickupLocation, "table.contract"),
        accessor: (group) => (group.contract ? contractSpanLabel(group.contract, t, locale) : "—"),
        cell: (info) => {
          const group = info.row.original;
          return group.contract ? (
            <Hyperlink
              className={LINK}
              onOpen={() => openContract(group.contract!, group.ownerOrganizationId)}
            >
              {info.getValue() as string}
            </Hyperlink>
          ) : (
            "—"
          );
        },
      },
      {
        id: "actions",
        header: "",
        width: 61,
        align: "center",
        cell: (info) => (
          <EditButton
            label={t(Namespace.PickupLocation, "modal.editContainerGroup")}
            onClick={() => setEditingGroup(info.row.original)}
          />
        ),
      },
    ],
    [t, locale, customerCell, openContract],
  );

  const byNormColumns = useMemo<Column<ConnectProto.Contract>[]>(
    () => [
      {
        id: "customer",
        header: t(Namespace.PickupLocation, "table.customer"),
        accessor: (contract) => contract.targetOrganization?.name ?? "",
        cell: (info) => customerCell(info.row.original.targetOrganization),
      },
      {
        id: "contractType",
        header: t(Namespace.PickupLocation, "participants.table.contractType"),
        accessor: (contract) => contractTypeLabel(contract.type, t),
      },
      {
        id: "contract",
        header: t(Namespace.PickupLocation, "table.contract"),
        accessor: (contract) => contractSpanLabel(contract, t, locale),
        cell: (info) => (
          <Hyperlink className={LINK} onOpen={() => openContract(info.row.original)}>
            {info.getValue() as string}
          </Hyperlink>
        ),
      },
      {
        id: "monthlyFee",
        header: t(Namespace.PickupLocation, "participants.table.monthlyFee"),
        accessor: (contract) => formatTenge(Number(contract.monthlyFee ?? 0)),
      },
      {
        id: "actions",
        header: "",
        width: 61,
        align: "center",
        cell: (info) => (
          <EditButton
            label={t(Namespace.PickupLocation, "participants.editTitle")}
            onClick={() => pickupLocation && openAttachModal(pickupLocation.id, info.row.original)}
          />
        ),
      },
    ],
    [t, locale, customerCell, openContract, openAttachModal, pickupLocation],
  );

  const empty = <TableEmpty>{t(Namespace.PickupLocation, "participants.empty")}</TableEmpty>;

  return (
    <div className="flex flex-col gap-8 h-full min-w-0 max-w-full overflow-y-auto">
      {!locationId && (
        <SaveLocationFirst>
          {t(Namespace.PickupLocation, "participants.saveLocationFirst")}
        </SaveLocationFirst>
      )}

      <Section title={t(Namespace.PickupLocation, "participants.ownersTitle")}>
        <LocationTableFrame>
          <DataTable
            columns={ownerColumns}
            data={containerGroups}
            rowKey={(group) => String(group.id)}
            rowClassName={() => "group"}
            loading={isLoading}
            empty={empty}
            namespace={Namespace.PickupLocation}
            className="border-0"
          />
        </LocationTableFrame>
      </Section>

      <Section title={t(Namespace.PickupLocation, "participants.byNormTitle")}>
        <LocationTableFrame>
          <DataTable
            columns={byNormColumns}
            data={subscriptionContracts}
            rowKey={(contract) => String(contract.id)}
            rowClassName={() => "group"}
            loading={isLocationFetching}
            empty={empty}
            namespace={Namespace.PickupLocation}
            className="border-0"
          />
        </LocationTableFrame>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start gap-1 !text-sm font-medium text-blue-600 hover:bg-blue-100"
          onClick={() => pickupLocation && openAttachModal(pickupLocation.id)}
          // The RPC replaces the whole set: attaching before the current set has
          // loaded would send only the new contract and unbind every other one.
          disabled={!pickupLocation}
        >
          <IcPlus className="w-4 h-4" />
          {t(Namespace.PickupLocation, "participants.attach")}
        </Button>
      </Section>

      <ContainerGroupModal
        isOpen={!!editingGroup}
        onClose={() => setEditingGroup(null)}
        onSubmit={(formData) =>
          submitGroup({
            formData,
            editingGroup,
            locationId: locationId ? BigInt(locationId) : undefined,
          })
        }
        locationId={locationId}
        editingGroup={editingGroup ?? undefined}
      />
    </div>
  );
};
